

class CollectionGrid extends HTMLElement {
  connectedCallback() {

    this.useAjax = this.dataset.useAjax;

    this.sectionId = this.dataset.sectionId;
    this.grid = this.querySelector("[data-grid]");
    this.prevButton = this.querySelector("[data-load-previous]");
    this.nextButton = this.querySelector("[data-load-next]");

    this.loadingPrev = false;
    this.loadingNext = false;
    this.currentVisiblePage = null;

    this.onPrevClick = this.onPrevClick.bind(this);
    this.onNextClick = this.onNextClick.bind(this);

    this.prevButton?.addEventListener("click", this.onPrevClick);
    this.nextButton?.addEventListener("click", this.onNextClick);

    this.collectionGridType();
    
    if (this.useAjax) {
      this.ensurePageAnchors();
      this.setupPageObserver();
    }
  }

  disconnectedCallback() {
    this.prevButton?.removeEventListener("click", this.onPrevClick);
    this.nextButton?.removeEventListener("click", this.onNextClick);
    this.pageObserver?.disconnect();
    this.mutationObserver?.disconnect();
  }

  async onPrevClick(event) {
    event.preventDefault();
    await this.loadPrevious();
  }

  async onNextClick(event) {
    event.preventDefault();
    await this.loadNext();
  }

  async fetchSection(url) {
    const requestUrl = new URL(url, window.location.origin);
    requestUrl.searchParams.set("section_id", this.sectionId);

    const response = await fetch(requestUrl.toString(), {
      headers: { "X-Requested-With": "XMLHttpRequest" }
    });

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}`);
    }

    const html = await response.text();
    const doc = new DOMParser().parseFromString(html, "text/html");
    const incomingGrid = doc.querySelector("collection-grid");

    return {
      gridInnerHtml: incomingGrid?.querySelector("[data-grid]")?.innerHTML ?? "",
      prevLink: incomingGrid?.querySelector("[data-load-previous]"),
      nextLink: incomingGrid?.querySelector("[data-load-next]")
    };
  }

  async loadNext() {
    if (this.loadingNext || !this.nextButton) return;
    this.loadingNext = true;

    try {
      const requestHref = this.nextButton.href;
      const requestedPage = this.getPageFromUrl(requestHref);

      const { gridInnerHtml, nextLink } = await this.fetchSection(requestHref);

      this.grid.insertAdjacentHTML("beforeend", gridInnerHtml);

      if (nextLink) {
        this.nextButton.href = nextLink.href;
      } else {
        this.nextButton.remove();
        this.nextButton = null;
      }

      this.refreshAfterDomChange();

      if (requestedPage) {
        this.updateUrlPage(requestedPage);
      }
    } catch (error) {
      console.error("loadNext error", error);
    } finally {
      this.loadingNext = false;
    }
  }

  async loadPrevious() {
    if (this.loadingPrev || !this.prevButton) return;
    this.loadingPrev = true;

    const anchor = this.getTopVisiblePageAnchor() || this.grid.firstElementChild;
    const beforeTop = anchor?.getBoundingClientRect().top ?? 0;

    try {
      const requestHref = this.prevButton.href;
      const requestedPage = this.getPageFromUrl(requestHref);

      const { gridInnerHtml, prevLink } = await this.fetchSection(requestHref);

      const temp = document.createElement("div");
      temp.innerHTML = gridInnerHtml;
      const nodes = Array.from(temp.children);

      for (let i = nodes.length - 1; i >= 0; i--) {
        this.grid.prepend(nodes[i]);
      }

      if (anchor) {
        const afterTop = anchor.getBoundingClientRect().top;
        window.scrollBy(0, afterTop - beforeTop);
      }

      if (prevLink) {
        this.prevButton.href = prevLink.href;
      } else {
        this.prevButton.remove();
        this.prevButton = null;
      }

      this.refreshAfterDomChange();

      if (requestedPage) {
        this.updateUrlPage(requestedPage);
      }
    } catch (error) {
      console.error("loadPrevious error", error);
    } finally {
      this.loadingPrev = false;
    }
  }

  refreshAfterDomChange() {
    this.ensurePageAnchors();
    this.refreshObservedItems();
    this.initCarousel();
  }

  getPageFromUrl(url) {
    try {
      const parsed = new URL(url, window.location.origin);
      return parsed.searchParams.get("page") || "1";
    } catch (e) {
      return "1";
    }
  }

  updateUrlPage(page) {
    const normalizedPage = String(page);
    if (this.currentVisiblePage === normalizedPage) return;

    const url = new URL(window.location.href);

    if (normalizedPage === "1") {
      url.searchParams.delete("page");
    } else {
      url.searchParams.set("page", normalizedPage);
    }

    history.replaceState({ page: normalizedPage }, "", url.toString());
    this.currentVisiblePage = normalizedPage;
  }

  ensurePageAnchors() {
    if (!this.grid) return;

    const items = Array.from(this.grid.querySelectorAll("[data-page]"));
    const seenPages = new Set();

    items.forEach((item) => {
      item.removeAttribute("data-page-anchor");
    });

    items.forEach((item) => {
      const page = item.dataset.page;
      if (!page || seenPages.has(page)) return;

      item.setAttribute("data-page-anchor", page);
      seenPages.add(page);
    });
  }

  setupPageObserver() {
    this.pageObserver?.disconnect();

    this.pageObserver = new IntersectionObserver(
      (entries) => {
        const visibleEntries = entries.filter((entry) => entry.isIntersecting);
        if (!visibleEntries.length) return;

        const bestEntry = visibleEntries.sort((a, b) => {
          const aDistance = Math.abs(a.boundingClientRect.top);
          const bDistance = Math.abs(b.boundingClientRect.top);
          return aDistance - bDistance;
        })[0];

        const page = bestEntry.target.getAttribute("data-page-anchor");
        if (!page) return;

        this.updateUrlPage(page);
      },
      {
        root: null,
        rootMargin: "-20% 0px -60% 0px",
        threshold: [0, 0.1, 0.25, 0.5, 0.75, 1]
      }
    );

    this.refreshObservedItems();

    this.mutationObserver?.disconnect();
    this.mutationObserver = new MutationObserver(() => {
      this.ensurePageAnchors();
      this.refreshObservedItems();
    });

    this.mutationObserver.observe(this.grid, { childList: true, subtree: false });
  }

  refreshObservedItems() {
    if (!this.pageObserver || !this.grid) return;

    this.pageObserver.disconnect();

    this.grid.querySelectorAll("[data-page-anchor]").forEach((item) => {
      this.pageObserver.observe(item);
    });
  }

  getTopVisiblePageAnchor() {
    const anchors = Array.from(this.grid.querySelectorAll("[data-page-anchor]"));

    const visibleAnchors = anchors
      .map((el) => ({
        el,
        rect: el.getBoundingClientRect()
      }))
      .filter(({ rect }) => rect.bottom > 0 && rect.top < window.innerHeight);

    if (!visibleAnchors.length) return null;

    visibleAnchors.sort((a, b) => Math.abs(a.rect.top) - Math.abs(b.rect.top));
    return visibleAnchors[0].el;
  }
  

  collectionGridType() {
    const mobileGrid = document.getElementById("grid-type");
    if (!mobileGrid || mobileGrid.dataset.collectionGridBound === "true") return;

    mobileGrid.dataset.collectionGridBound = "true";

    mobileGrid.addEventListener("click", (e) => {
      e.preventDefault();

      const iconGrid = mobileGrid.querySelector(".icon-grid");
      const iconList = mobileGrid.querySelector(".icon-list");
      const collectionProducts = document.querySelector(".collection-products");

      if (!collectionProducts) return;

      if (collectionProducts.classList.contains("grid")) {
        collectionProducts.classList.remove("grid");
        if (iconGrid) iconGrid.hidden = true;
        if (iconList) iconList.hidden = false;
      } else {
        collectionProducts.classList.add("grid");
        if (iconGrid) iconGrid.hidden = false;
        if (iconList) iconList.hidden = true;
      }
    });
  }
}

if (!customElements.get("collection-grid")) {
  customElements.define("collection-grid", CollectionGrid);
}